import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { useSupportedLocale } from '@flipster/i18n/hooks';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';

import { rewardHubV2Bunja } from '../../../state';
import { RegularMission } from '../../../types';
import { DistributionDate } from '../../misc';
import {
  getIsDepositAmountCondition,
  getLocalizedText,
  getMissionStatusFlags,
  getRewardAmount,
  getRewardCurrency,
} from '../../utils';
import { MilestoneIcon } from './mobile-milestone-item';

interface DisabledListProps {
  title: ReactNode;
  missions: RegularMission[];
}

interface DisabledItemProps {
  mission: RegularMission;
}

export const DisabledList = ({ title, missions }: DisabledListProps) => {
  if (!missions || !missions.length) return null;

  return (
    <>
      <div className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-3', mt: 16 })}>{title}</div>
      {missions.map((mission, index) => (
        <DisabledItem key={`disabled-mission-${index}`} mission={mission} />
      ))}
    </>
  );
};

const DisabledItem = ({ mission }: DisabledItemProps) => {
  const locale = useSupportedLocale();
  const { feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const skin = mission.skin;
  const status = mission.status;
  const completionReward = mission.spec.completionRewards[0];
  const { isExpired, isRejected } = getMissionStatusFlags(status);

  const missionTitle = getLocalizedText(skin?.body?.localizationTable, locale);

  return (
    <div className={center({ gap: 16, py: 16 })}>
      <MilestoneIcon status={status} completionConditions={mission.spec.completionConditions} isActive={false} />
      <div className={css({ flex: 1 })}>
        <div
          className={stack({
            textStyle: 'Text/L/Strong',
            color: 'content.default.level-1',
          })}
        >
          <Formatted.Number
            value={getRewardAmount(completionReward, feeVoucherRewardRecord)}
            precision={0}
            toFixed
            css={{
              textStyle: 'Text/S/Strong',
              color: 'content.default.level-1',
            }}
            suffix={getRewardCurrency(completionReward)}
          />
          {missionTitle}
        </div>
        <DistributionDate
          date={status?.settleAt}
          showMaintainMessage={mission.spec.completionConditions.some(getIsDepositAmountCondition)}
          isHide={isExpired || isRejected}
        />
      </div>
    </div>
  );
};
